/* eslint-disable @typescript-eslint/no-restricted-imports */ // TODO: Don't use 'modules' import
import React, { ReactNode } from 'react'

import { isFractionFalsy } from '@cowprotocol/common-utils'
import { Currency, CurrencyAmount } from '@cowprotocol/currency'
import { TEST_IDS } from '@cowprotocol/test-ids'
import { TokenAmount } from '@cowprotocol/ui'

import { t } from '@lingui/core/macro'
import { Trans } from '@lingui/react/macro'

import { BalanceAndSubsidy } from 'legacy/hooks/useCowBalanceAndSubsidy'

import { getOrderTypeReceiveAmounts, useIsEoaEthFlow, useIsFreeOrderPlacement, ReceiveAmountInfo } from 'modules/trade'

import { FeeItem } from './FeeItem'
import { NetworkFeeItem } from './NetworkFeeItem'
import * as styledEl from './styled'

export interface ReceiveAmountInfoTooltipProps {
  receiveAmountInfo: ReceiveAmountInfo
  subsidyAndBalance: BalanceAndSubsidy
  allowsOffchainSigning: boolean
}

export function ReceiveAmountInfoTooltip(props: ReceiveAmountInfoTooltipProps): ReactNode {
  const isEoaEthFlow = useIsEoaEthFlow()
  const isFreePlacement = useIsFreeOrderPlacement()

  const { receiveAmountInfo, subsidyAndBalance, allowsOffchainSigning } = props
  const {
    isSell,
    costs: {
      partnerFee: { amount: partnerFeeAmount, bps: partnerFeeBps },
      protocolFee,
      bridgeFee,
    },
    beforeAllFees,
  } = receiveAmountInfo
  const { amountAfterFees, networkFeeAmount } = getOrderTypeReceiveAmounts(receiveAmountInfo)
  const { subsidy } = subsidyAndBalance
  const { discount } = subsidy

  const protocolFeeAmount = protocolFee?.amount
  const protocolFeeBps = protocolFee?.bps

  const hasPartnerFee = hasValidFee(partnerFeeAmount, partnerFeeBps)
  const hasProtocolFee = hasValidFee(protocolFeeAmount, protocolFeeBps)
  const hasAnyFee = hasPartnerFee || hasProtocolFee
  const hasNetworkFee = !isFractionFalsy(networkFeeAmount)
  const hasFee = hasNetworkFee || hasAnyFee

  const isEoaNotEthFlow = allowsOffchainSigning && !isEoaEthFlow

  const beforeAllFeesAmount = isSell ? beforeAllFees.buyAmount : beforeAllFees.sellAmount

  return (
    <styledEl.Box>
      <div data-testid={TEST_IDS.beforeCosts}>
        <span>
          <Trans>Before costs</Trans>
        </span>
        <span>
          <TokenAmount amount={beforeAllFeesAmount} tokenSymbol={beforeAllFeesAmount.currency} defaultValue="0" />
        </span>
      </div>

      {hasProtocolFee && (
        <FeeItem title={t`Protocol fee`} isSell={isSell} feeAmount={protocolFeeAmount} testId={TEST_IDS.protocolFee} />
      )}

      {hasPartnerFee && (
        <FeeItem title={t`Partner fee`} isSell={isSell} feeAmount={partnerFeeAmount} testId={TEST_IDS.partnerFee} />
      )}

      <NetworkFeeItem
        discount={discount}
        networkFeeAmount={networkFeeAmount}
        isSell={isSell}
        hasFee={hasFee}
        isFreePlacement={isFreePlacement}
        testId={TEST_IDS.networkCosts}
      />

      {!hasAnyFee && !isEoaNotEthFlow && isFreePlacement && (
        <FeeItem title={t`Fee`} isSell={isSell} feeAmount={undefined} testId={TEST_IDS.freeFee} />
      )}

      {bridgeFee && (
        <FeeItem
          title={t`Bridge costs`}
          isSell={isSell}
          feeAmount={bridgeFee?.amountInDestinationCurrency}
          testId={TEST_IDS.bridgeCosts}
        />
      )}

      {!isFractionFalsy(amountAfterFees) && (
        <styledEl.TotalAmount data-testid={TEST_IDS.receiveAmountTotal}>
          <span>{!isSell ? t`From` : t`To`}</span>
          <span>
            <TokenAmount amount={amountAfterFees} tokenSymbol={amountAfterFees.currency} defaultValue="0" />
          </span>
        </styledEl.TotalAmount>
      )}
    </styledEl.Box>
  )
}

function hasValidFee(amount: CurrencyAmount<Currency> | undefined, bps: number | undefined): boolean {
  return !!amount && !!bps && !amount.equalTo(0)
}
